// Saved-search persistence layer (decision 8 — local; alerts deferred to
// Phase 1.5 per decision 15). Stored in localStorage under a versioned key so
// we can migrate cleanly later. The view is whatever URL query string is in
// effect when the user saves — same shape useUnitsQueryState parses.

import type { UnitsQuery } from '$lib/api/types';

export interface SavedSearch {
  id: string;
  name: string;
  /** URL search string with leading '?'. */
  search: string;
  /** Cached summary of the parsed query at save time, used for list previews. */
  summary: SavedSearchSummary;
  createdAt: string;
  updatedAt: string;
}

export interface SavedSearchSummary {
  q?: string;
  postnummerCount?: number;
  m2Range?: [number?, number?];
  roomsRange?: [number?, number?];
  yearRange?: [number?, number?];
  priceRange?: [number?, number?];
  statusCount?: number;
  sourceCount?: number;
  filterCount: number;
  sort?: string;
  dir?: string;
}

const STORAGE_KEY = 'rentallens.savedSearches.v1';
export const STORAGE_VERSION = 1;
export const SAVED_SEARCHES_STORAGE_KEY = STORAGE_KEY;

interface StoredEnvelope {
  version: number;
  items: SavedSearch[];
}

function readEnvelope(): StoredEnvelope {
  if (typeof window === 'undefined') return { version: STORAGE_VERSION, items: [] };
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return { version: STORAGE_VERSION, items: [] };
  try {
    const parsed = JSON.parse(raw) as Partial<StoredEnvelope>;
    if (parsed && parsed.version === STORAGE_VERSION && Array.isArray(parsed.items)) {
      return { version: STORAGE_VERSION, items: parsed.items.filter(isSavedSearch) };
    }
  } catch {
    // fallthrough — wipe the slot if corrupted
  }
  return { version: STORAGE_VERSION, items: [] };
}

function writeEnvelope(env: StoredEnvelope): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(env));
  // Tell same-tab subscribers; the native 'storage' event fires only across tabs.
  window.dispatchEvent(new CustomEvent('rentallens:savedSearches'));
}

function isSavedSearch(value: unknown): value is SavedSearch {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<SavedSearch>;
  return (
    typeof v.id === 'string' &&
    typeof v.name === 'string' &&
    typeof v.search === 'string' &&
    typeof v.createdAt === 'string' &&
    typeof v.updatedAt === 'string' &&
    !!v.summary &&
    typeof v.summary === 'object'
  );
}

function uid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function listSavedSearches(): SavedSearch[] {
  return [...readEnvelope().items].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getSavedSearch(id: string): SavedSearch | null {
  return readEnvelope().items.find((x) => x.id === id) ?? null;
}

export interface CreateSavedSearchInput {
  name: string;
  search: string;
  query: UnitsQuery;
}

export function createSavedSearch(input: CreateSavedSearchInput): SavedSearch {
  const env = readEnvelope();
  const now = new Date().toISOString();
  const trimmed = input.name.trim() || 'Untitled search';
  const item: SavedSearch = {
    id: uid(),
    name: ensureUniqueName(trimmed, env.items),
    search: normalizeSearch(input.search),
    summary: summarize(input.query),
    createdAt: now,
    updatedAt: now
  };
  writeEnvelope({ version: STORAGE_VERSION, items: [item, ...env.items] });
  return item;
}

export interface UpdateSavedSearchInput {
  id: string;
  name?: string;
  search?: string;
  query?: UnitsQuery;
}

export function updateSavedSearch(input: UpdateSavedSearchInput): SavedSearch | null {
  const env = readEnvelope();
  const idx = env.items.findIndex((x) => x.id === input.id);
  if (idx === -1) return null;
  const current = env.items[idx];
  const others = env.items.filter((_, i) => i !== idx);
  const next: SavedSearch = {
    ...current,
    name: input.name != null ? ensureUniqueName(input.name.trim() || current.name, others) : current.name,
    search: input.search != null ? normalizeSearch(input.search) : current.search,
    summary: input.query ? summarize(input.query) : current.summary,
    updatedAt: new Date().toISOString()
  };
  const items = [...env.items];
  items[idx] = next;
  writeEnvelope({ version: STORAGE_VERSION, items });
  return next;
}

export function deleteSavedSearch(id: string): boolean {
  const env = readEnvelope();
  const next = env.items.filter((x) => x.id !== id);
  if (next.length === env.items.length) return false;
  writeEnvelope({ version: STORAGE_VERSION, items: next });
  return true;
}

export function clearSavedSearches(): void {
  writeEnvelope({ version: STORAGE_VERSION, items: [] });
}

function normalizeSearch(search: string): string {
  if (!search) return '';
  return search.startsWith('?') ? search : `?${search}`;
}

function ensureUniqueName(candidate: string, existing: SavedSearch[]): string {
  const taken = new Set(existing.map((x) => x.name.toLowerCase()));
  if (!taken.has(candidate.toLowerCase())) return candidate;
  let n = 2;
  while (taken.has(`${candidate.toLowerCase()} (${n})`)) n += 1;
  return `${candidate} (${n})`;
}

function summarize(query: UnitsQuery): SavedSearchSummary {
  let filterCount = 0;
  if (query.q) filterCount += 1;
  if (query.postnummer?.length) filterCount += 1;
  if (query.m2Min != null || query.m2Max != null) filterCount += 1;
  if (query.roomsMin != null || query.roomsMax != null) filterCount += 1;
  if (query.yearMin != null || query.yearMax != null) filterCount += 1;
  if (query.priceMin != null || query.priceMax != null) filterCount += 1;
  if (query.status?.length) filterCount += 1;
  if (query.source?.length) filterCount += 1;
  return {
    q: query.q,
    postnummerCount: query.postnummer?.length ?? undefined,
    m2Range: query.m2Min != null || query.m2Max != null ? [query.m2Min, query.m2Max] : undefined,
    roomsRange: query.roomsMin != null || query.roomsMax != null ? [query.roomsMin, query.roomsMax] : undefined,
    yearRange: query.yearMin != null || query.yearMax != null ? [query.yearMin, query.yearMax] : undefined,
    priceRange: query.priceMin != null || query.priceMax != null ? [query.priceMin, query.priceMax] : undefined,
    statusCount: query.status?.length ?? undefined,
    sourceCount: query.source?.length ?? undefined,
    filterCount,
    sort: query.sort,
    dir: query.dir
  };
}

export const __test = { summarize, ensureUniqueName, normalizeSearch };
